import AdminService from '../services/AdminService';
import ChatService from '../../../../services/ChatService';
import { useMemo } from 'react';
import { useAuth } from '../../../../contexts/auth/AuthContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AdminResponseType, UtilityAudienceRequest, UtilityAudienceSearchRequest, UtilityAudienceUser } from '../types/AdminServiceTypes';
import { AdminQueryKeys } from '../../../../shared/react-query/enums/AdminQueryKeys';
import { useBackendEvents } from '../../../../contexts/backend-events/BackendEventsContext';
import { RoutingPathsType } from '../helpers/routing';
import { queryClientInstance } from '../../../../shared/react-query';
import { AssetsService, GetImageRequestQuery } from '../../../../services/AssetService';
import { WithRevision } from '../sections/assistants/types/AssistantsServiceTypes';

let adminServiceInstance: AdminService | null = null;

const useAdminService = () => {
    const { getAccessToken } = useAuth();

    const service = useMemo(() => {
        if (!adminServiceInstance) adminServiceInstance = new AdminService(getAccessToken);
        return adminServiceInstance;
    }, [getAccessToken]);
    return { service };
};

const useAdminChatService = () => {
    const { getAccessToken } = useAuth();
    const backendEvents = useBackendEvents();
    // TODO: Detach File upload from chat service to its own
    const service = useMemo(() => new ChatService(getAccessToken, backendEvents), [getAccessToken, backendEvents]);
    return { service };
};

const useAssetsService = () => {
    const { getAccessToken } = useAuth();
    // TODO: Move to right folder upload from chat service to its own
    const service = useMemo(() => new AssetsService(getAccessToken), [getAccessToken]);
    return { service };
};


// Documents

export const useGetDocument = ({ assistantId, versionNumber, fileIds, personaId }: RoutingPathsType['versions'] & { fileIds?: string[], personaId: string; }) => {
    const { service } = useAdminChatService();
    return useQuery({
        queryKey: AdminQueryKeys.assistantVersionFiles({ assistantId, versionNumber }),
        staleTime: 0,
        queryFn: () => service.getDocuments({ fileIds, personaId }),
    });
};

export const useUploadDocument = ({ assistantId, versionNumber, revisionKey }: RoutingPathsType['versions'] & WithRevision) => {
    const { service } = useAdminChatService();
    return useMutation({
        mutationFn: service.uploadDocument,
        onSuccess: async () => {
            await queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersionFiles({ assistantId, versionNumber }) });
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersionRevision({ assistantId, versionNumber, revisionKey }) });
        }
    });
};

export const useDownloadDocuments = () => {
    const { service } = useAdminChatService();
    return useMutation({
        mutationFn: service.downloadDocuments,
    });
};

export const useUpdateDocumentMetadata = ({ assistantId, versionNumber, revisionKey }: RoutingPathsType['versions'] & WithRevision) => {
    const { service } = useAdminChatService();
    return useMutation({
        mutationFn: service.updateDocumentMetadata,
        onSuccess: async () => {
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersionFiles({ assistantId, versionNumber }) });
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersionRevision({ assistantId, versionNumber, revisionKey }) });
        }
    });
};

export const useDeleteDocuments = ({ assistantId, versionNumber, revisionKey }: RoutingPathsType['versions'] & WithRevision) => {
    const { service } = useAdminChatService();
    return useMutation({
        mutationFn: service.deleteDocuments,
        onSuccess: async () => {
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersionFiles({ assistantId, versionNumber }) });
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersionRevision({ assistantId, versionNumber, revisionKey }) });
        }
    });
};


// Images
export const useGetImageFullUrl = () => {
    const { service } = useAssetsService();
    const result = useMemo(() => ({
        getFullImageUrl: (imagePath: string, options?: GetImageRequestQuery) => service.getImageFullUrl(imagePath, options)
    }), [service]);

    return result;
};

export const useUploadImage = () => {
    const { service } = useAssetsService();
    return useMutation({
        mutationFn: service.uploadImage
    });
};


// Admin

const useGetAdmin = <TSelect extends AdminResponseType, TResult = TSelect>({ select }: { select: (props: AdminResponseType) => TResult; }) => {
    const { service } = useAdminService();
    return useQuery({
        queryKey: AdminQueryKeys.permission(),
        queryFn: service.getAdminPermission,
        select
    });
};

export const useGetAdminPermission = () => {
    return useGetAdmin({
        select: ({ permissions }) => permissions
    });
};

export const useGetDefinitions = () => {
    const { service } = useAdminService();
    return useQuery({
        queryKey: AdminQueryKeys.definitions(),
        queryFn: service.getAdminDefinitions,
    });
};


// Utility

export const useGetUtilityAudience = (props: UtilityAudienceRequest) => {
    const { service } = useAdminService();
    return useQuery({
        queryKey: AdminQueryKeys.audience({ ...props }),
        queryFn: () => service.getUtilityAudience(props),
        staleTime: 0,
    });
};
export const useGetUtilityAudienceSearch = (props: UtilityAudienceSearchRequest) => {
    const { service } = useAdminService();
    return useQuery({
        queryKey: AdminQueryKeys.audienceSearch({ ...props }),
        queryFn: () => service.getUtilityAudienceSearch(props),
        enabled: !!props?.enabled,
        staleTime: 0,
    });
};

export const useAddDanfossApp = () => {
    const { service } = useAdminService();
    return useMutation({
        mutationFn: (model: UtilityAudienceUser) => service.addDanfossApp(model),
    });
};

export const useGetEvaluationLevels = () => {
    const { service } = useAdminService();
    return useQuery({
        queryKey: AdminQueryKeys.evaluationLevels(),
        queryFn: () => service.getUtilityEvaluationLevels(),
    });
};
